import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ModalAdminEditAnalysis from './ModalAdminEditAnalysis';
import {
  fetchApiRaw,
  GET_ANALYSES_CMS,
  GET_ANALYSES,
} from 'r/utils/fetchApiRaw';
import type { Analyse } from 'r/types';
import { useScoreMarketByAnalysis } from '../ScoreMarketsTab/queries';

export default function AnalysisEditRoute() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // --- Analysis ---
  const cachedAnalysis = queryClient
    .getQueriesData<{ data: Analyse[] }>({ queryKey: ['cms-analyses'] })
    .flatMap(([, page]) => page?.data ?? [])
    .find((a) => a.id === analysisId);

  const { data: fetchedAnalysis } = useQuery({
    queryKey: ['cms-analysis', analysisId],
    queryFn: async () => {
      const res = await fetchApiRaw(GET_ANALYSES_CMS, {
        queryParams: {
          filter: encodeURIComponent(`"id":["${analysisId}"]`),
          take: '1',
        },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      return (data[0] as Analyse) ?? null;
    },
    enabled: !cachedAnalysis && !!analysisId,
    staleTime: 5 * 60 * 1000,
  });

  const analysis = cachedAnalysis ?? fetchedAnalysis ?? undefined;
  const tweetID = analysis?.tweetID;

  // --- Sanitizer analysis ---
  const cachedSanitizer =
    tweetID ?
      queryClient
        .getQueriesData<{ data: Analyse[] }>({
          queryKey: ['play-analyses-sanitizer'],
        })
        .flatMap(([, page]) => page?.data ?? [])
        .find((a) => a.tweetID === tweetID)
    : undefined;

  const { data: fetchedSanitizer } = useQuery({
    queryKey: ['sanitizer-by-tweet', tweetID],
    queryFn: async () => {
      const res = await fetchApiRaw(GET_ANALYSES, {
        queryParams: {
          filter: encodeURIComponent(`"tweetID":["${tweetID}"]`),
          take: '1',
        },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      return (data[0] as Analyse) ?? null;
    },
    enabled: !cachedSanitizer && !!tweetID,
    staleTime: 5 * 60 * 1000,
  });

  const sanitizerAnalysis = cachedSanitizer ?? fetchedSanitizer ?? undefined;

  // --- Existing Score market ---
  const { data: existingMarket } = useScoreMarketByAnalysis(analysisId);

  const handleClose = () => {
    if (location.state?.background) {
      navigate(-1);
    } else {
      navigate('/?tab=analyses');
    }
  };

  if (!analysis) return null;

  return (
    <ModalAdminEditAnalysis
      analysis={analysis}
      sanitizerAnalysis={sanitizerAnalysis}
      existingMarket={existingMarket}
      onClose={handleClose}
      onAnalysisUpdated={() => {}}
      onDelete={handleClose}
    />
  );
}
